import { useEffect, useRef } from "react";
import { useTranslate } from "ra-core";
import { registerSW } from "virtual:pwa-register";

import { showToast } from "@/components/ui/toast";
import {
  hasUnsavedInput,
  startUnsavedInputTracking,
} from "../misc/unsavedInput";

// PWA-oppdaterings-toast (veikartets F1): service workeren registreres i
// «prompt»-modus, og når en ny versjon er lastet ned vises en toast med
// «Oppdater»-knapp som aktiverer den og laster siden på nytt. Uten dette
// serverte PWA-en gammel bundle til neste harde reload — den klassiske
// «hvorfor ser jeg ikke endringen?»-fella.
//
// Nye versjoner oppdages også i langlivede faner: vi sjekker hvert kvarter
// og hver gang fanen får fokus igjen (typisk «tilbake fra e-posten»).
// I dev er registerSW en no-op fra VitePWA; i vitest er modulen stubbet
// (se vitest.config.ts).
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
// Hvor ofte vi ser etter om ulagret tekst er lagret, når oppdateringen venter.
const WAIT_POLL_MS = 2000;

// Modulvakt: registrering skal skje én gang per sesjon, selv om
// layouten (desktop/mobil) remonteres.
let registered = false;

export const PwaUpdateToast = () => {
  const translate = useTranslate();
  const translateRef = useRef(translate);
  translateRef.current = translate;

  useEffect(() => {
    if (registered || !("serviceWorker" in navigator)) return;
    registered = true;
    startUnsavedInputTracking();

    // Vakt mot toast-stabling når flere update-hendelser fyres.
    let toastOpen = false;

    const updateSW = registerSW({
      onNeedRefresh() {
        if (toastOpen) return;
        toastOpen = true;
        const t = translateRef.current;
        showToast(
          t("crm.pwa.update_title", { _: "Ny versjon av Mimin er klar" }),
          {
            description: t("crm.pwa.update_description", {
              _: "Oppdater for å få de siste endringene.",
            }),
            // Helfarget teal-flate (Vercel-stil), forbeholdt denne meldingen.
            variant: "brand",
            duration: Infinity,
            dismissLabel: t("crm.pwa.later", { _: "Senere" }),
            action: {
              label: t("crm.pwa.update", { _: "Oppdater" }),
              onClick: () => {
                // En oppdatering laster siden på nytt. Står det ulagret tekst
                // i et skjema, venter vi til den er lagret eller forkastet.
                if (!hasUnsavedInput()) {
                  void updateSW(true);
                  return;
                }
                const timer = window.setInterval(() => {
                  if (hasUnsavedInput()) return;
                  window.clearInterval(timer);
                  void updateSW(true);
                }, WAIT_POLL_MS);
                showToast(
                  t("crm.pwa.waiting_title", {
                    _: "Du har tekst som ikke er lagret",
                  }),
                  {
                    description: t("crm.pwa.waiting_description", {
                      _: "Mimin oppdateres når du har lagret eller forkastet den.",
                    }),
                    variant: "info",
                    duration: Infinity,
                    dismissLabel: t("ra.action.close", { _: "Lukk" }),
                    action: {
                      label: t("crm.pwa.update_anyway", {
                        _: "Oppdater likevel",
                      }),
                      onClick: () => {
                        window.clearInterval(timer);
                        void updateSW(true);
                      },
                    },
                  },
                );
              },
            },
            onClose: () => {
              toastOpen = false;
            },
          },
        );
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;
        const check = () => {
          void registration.update().catch(() => {
            // Nettverksfeil under sjekk er uinteressant – prøves igjen.
          });
        };
        setInterval(check, CHECK_INTERVAL_MS);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
      },
    });
  }, []);

  return null;
};
